import React, { Component } from "react";
import "../../App.css";

class TableHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openDropdown: null, // Track which dropdown is open
      dropdownPosition: { top: 0, left: 0, maxHeight: null, openUpward: false },
    };
    this.filterButtonRefs = {}; // Store refs to filter buttons
  }

  componentDidMount = () => {
    let columnsList = [];
    this.props.columns.forEach((element) => {
      if (typeof element.label === "string" && element.path !== undefined)
        columnsList.push(element);
    });
    this.props.updateColumnList(columnsList);

    // Close dropdown when clicking outside
    document.addEventListener("click", this.handleOutsideClick);
    // Close dropdown when scrolling (since it's fixed position)
    window.addEventListener("scroll", this.handleScroll, true);
  };

  componentDidUpdate = (prevProps) => {
    // Handle external request to open a filter dropdown
    if (
      this.props.externalOpenFilter &&
      this.props.externalOpenFilter !== prevProps.externalOpenFilter
    ) {
      const filterKey = this.props.externalOpenFilter;
      if (this.filterButtonRefs[filterKey]) {
        this.setState({
          openDropdown: filterKey,
          dropdownPosition: this.calcDropdownPosition(filterKey),
        });
      }
      // Clear the external trigger
      if (this.props.onExternalFilterOpened) {
        this.props.onExternalFilterOpened();
      }
    }
  };

  componentWillUnmount = () => {
    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("scroll", this.handleScroll, true);
  };

  handleScroll = (e) => {
    // Don't close dropdown if scrolling inside it
    if (e.target.closest && e.target.closest(".column-filter-dropdown")) {
      return;
    }
    if (this.state.openDropdown) {
      this.setState({ openDropdown: null });
    }
  };

  handleOutsideClick = (e) => {
    if (!e.target.closest(".column-filter-dropdown")) {
      this.setState({ openDropdown: null });
    }
  };

  raiseSort = (path) => {
    const sortColumn = { ...this.props.sortColumn };
    if (sortColumn.path === path)
      sortColumn.order = sortColumn.order === "asc" ? "desc" : "asc";
    else {
      sortColumn.path = path;
      sortColumn.order = "asc";
    }
    if (
      sortColumn.path
        ? sortColumn.path !== undefined
        : sortColumn.key !== undefined
    ) {
      this.props.onSort(sortColumn);
    }
  };

  renderSortIcon = (column) => {
    const { sortColumn } = this.props;
    if (
      (column.path !== sortColumn.path && column.key !== sortColumn.path) ||
      sortColumn.path === undefined
    )
      return null;
    if (sortColumn.order === "asc")
      return <span style={{ margin: "auto", fontSize: "10px" }}>&#9660;</span>;
    return <span style={{ margin: "auto", fontSize: "10px" }}>&#9650;</span>;
  };

  renderCell = (column) => {
    if (typeof column.label !== "string") return column.label(column);
    return column.label;
  };

  /**
   * Calculate viewport-clamped position for a filter dropdown.
   * Opens downward by default; flips upward if insufficient space below.
   */
  calcDropdownPosition = (filterKey) => {
    const rect = this.filterButtonRefs[filterKey].getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const margin = 10; // px buffer from viewport edges
    const preferredMax =
      filterKey === "financial-professional" ? 450 : 300;

    const spaceBelow = viewportH - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUpward =
      spaceBelow < Math.min(preferredMax, 200) && spaceAbove > spaceBelow;

    const maxHeight = Math.min(
      preferredMax,
      openUpward ? spaceAbove : spaceBelow,
    );

    const minWidth =
      filterKey === "financial-professional" ? 300 : 180;
    const left = Math.min(rect.left, viewportW - minWidth - margin);

    return {
      top: openUpward ? undefined : rect.bottom + 2,
      bottom: openUpward ? viewportH - rect.top + 2 : undefined,
      left: Math.max(margin, left),
      maxHeight,
      openUpward,
    };
  };

  toggleDropdown = (e, filterKey) => {
    e.stopPropagation();
    const isOpening = this.state.openDropdown !== filterKey;

    if (isOpening && this.filterButtonRefs[filterKey]) {
      this.setState({
        openDropdown: filterKey,
        dropdownPosition: this.calcDropdownPosition(filterKey),
      });
    } else {
      this.setState({ openDropdown: null });
    }
  };

  /**
   * Gets unique filter options from data for a column
   */
  getFilterOptions = (column) => {
    const { data, defaultFilterOptions, financialProfessionals } = this.props;
    const path = column.path || column.key;
    const filterKey = column.filterKey || path;

    // For financial-professional filter:
    // - Admin/compliance users: use financialProfessionals array (formatted as "(ID) Name")
    // - Non-admin users: generate options from actual advisor-name values in the data
    if (filterKey === "financial-professional") {
      // Admin/compliance users have the full financialProfessionals list
      if (financialProfessionals && financialProfessionals.length > 0) {
        return financialProfessionals;
      }

      // Non-admin users: generate options from the advisor-name values in the data
      if (!data || !Array.isArray(data)) return [];

      const uniqueAdvisors = new Set();
      data.forEach((item) => {
        const value = item["advisor-name"] || item["group-id"];
        if (value !== undefined && value !== null && value !== "") {
          // Handle multi-advisor values (separated by / or ;)
          const parts = String(value).split(/[\/;]/).map(p => p.trim()).filter(p => p);
          parts.forEach(part => uniqueAdvisors.add(part));
        }
      });

      return Array.from(uniqueAdvisors).sort();
    }

    // Check for default options first (for other columns)
    if (defaultFilterOptions && defaultFilterOptions[filterKey]) {
      return defaultFilterOptions[filterKey].filter((opt) => opt !== "All");
    }

    if (!data || !Array.isArray(data)) return [];

    // For other columns, use the column path
    const uniqueValues = new Set();
    data.forEach((item) => {
      const value = item[path];
      if (value !== undefined && value !== null && value !== "") {
        uniqueValues.add(String(value));
      }
    });

    return Array.from(uniqueValues).sort();
  };

  /**
   * Check if a value is selected in the filter
   */
  isValueSelected = (filterKey, value) => {
    const { filterState } = this.props;
    if (!filterState) return false;
    const selected = filterState[filterKey] || [];
    const isSelected = selected.includes(value);
    // Only log for financial-professional to avoid too much noise
    if (filterKey === "financial-professional" && isSelected) {
      console.log("isValueSelected TRUE for:", value);
    }
    return isSelected;
  };

  /**
   * Get count of selected filters for a column
   * Only counts values that exist in the current options
   */
  getSelectedCount = (filterKey, column) => {
    const { filterState } = this.props;
    if (!filterState) return 0;
    const selected = filterState[filterKey] || [];

    // Get available options for this filter
    const options = this.getFilterOptions(column);

    // Only count values that exist in the current options
    // This prevents showing stale counts when options change (e.g., after page refresh)
    const validSelected = selected.filter((v) => options.includes(v));
    return validSelected.length;
  };

  /**
   * Handle checkbox change for a filter option
   */
  handleCheckboxChange = (filterKey, value, isChecked) => {
    const { onFilterChange, filterState } = this.props;
    if (!onFilterChange) return;

    const currentValues = filterState?.[filterKey] || [];
    let newValues;

    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter((v) => v !== value);
    }

    console.log("=== CHECKBOX CHANGE ===");
    console.log("filterKey:", filterKey);
    console.log("value clicked:", value);
    console.log("isChecked:", isChecked);
    console.log("currentValues before:", currentValues);
    console.log("newValues after:", newValues);
    console.log("newValues length:", newValues.length);

    onFilterChange(filterKey, newValues);
  };

  /**
   * Select all options for a column
   */
  handleSelectAll = (filterKey, options) => {
    const { onFilterChange } = this.props;
    if (!onFilterChange) return;
    onFilterChange(filterKey, options);
  };

  /**
   * Clear all options for a column
   */
  handleClearFilter = (filterKey) => {
    const { onFilterChange } = this.props;
    if (!onFilterChange) return;
    onFilterChange(filterKey, []);
    this.setState({ openDropdown: null });
  };

  renderFilterDropdown = (column) => {
    const { openDropdown, dropdownPosition } = this.state;

    // Skip non-filterable columns
    if (!column.filterable) return null;

    const path = column.path || column.key;
    const filterKey = column.filterKey || path;
    const options = this.getFilterOptions(column);
    const selectedCount = this.getSelectedCount(filterKey, column);
    const isOpen = openDropdown === filterKey;

    return (
      <div
        className="column-filter-dropdown"
        style={{ display: "inline-block" }}
      >
        <button
          type="button"
          ref={(el) => (this.filterButtonRefs[filterKey] = el)}
          className={`column-filter-btn ${selectedCount > 0 ? "active" : ""}`}
          onClick={(e) => this.toggleDropdown(e, filterKey)}
          title={`Filter by ${column.label}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z" />
          </svg>
          {selectedCount > 0 && <span className="ms-1">{selectedCount}</span>}
        </button>

        {isOpen && (
          <div
            className="dropdown-menu show"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              bottom: dropdownPosition.bottom,
              left: dropdownPosition.left,
              zIndex: 1050,
              minWidth: filterKey === "financial-professional" ? "300px" : "180px",
              ...(options.length > 0 && {
                maxHeight: dropdownPosition.maxHeight || 300,
                overflowY: "auto",
              }),
              padding: "8px",
              fontWeight: "normal",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Select All / Clear buttons */}
            <div className="d-flex justify-content-between mb-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => this.handleSelectAll(filterKey, options)}
              >
                Select All
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-danger"
                onClick={() => this.handleClearFilter(filterKey)}
              >
                Clear
              </button>
            </div>
            <hr className="my-1" />

            {/* Checkbox list */}
            {options.length === 0 ? (
              <span className="text-muted">
                No options
              </span>
            ) : (
              options.map((option) => (
                <div key={option} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`filter-${filterKey}-${option}`}
                    checked={this.isValueSelected(filterKey, option)}
                    onChange={(e) =>
                      this.handleCheckboxChange(filterKey, option, e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`filter-${filterKey}-${option}`}
                    style={{ cursor: "pointer" }}
                  >
                    {option || "(empty)"}
                  </label>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  render() {
    return (
      <thead>
        <tr className="vicus-blue">
          {this.props.columns &&
            this.props.columns.map((column, index) => (
              <th
                className="clickable"
                key={column.path + `${index}` || column.key + `${index}`}
                style={{
                  position: "relative",
                  userSelect: "none",
                  cursor: column.sortable !== false ? "pointer" : "default",
                }}
                onClick={() => {
                  if (column.sortable !== false) {
                    this.raiseSort(column.path ? column.path : column.key);
                  }
                }}
              >
                <div className="header-cell-inner">
                  <span className="header-sort-region">
                    {this.renderCell(column)} {this.renderSortIcon(column)}
                  </span>
                  {column.filterable && (
                    <div className="header-filter-region">
                      {this.renderFilterDropdown(column)}
                    </div>
                  )}
                </div>
              </th>
            ))}
        </tr>
      </thead>
    );
  }
}

export default TableHeader;
